import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { Plus } from 'lucide-react';
import { useTranslation } from '@/lib/i18n';
import {
  Button, PageShell, Skeleton, DataTable, StatusBadge, RowActions, Pagination,
  FilterBar, FilterField, Input, RadixSelect, type DataTableColumn,
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogClose,
} from '@/components/ui';
import { useFaqsQuery, useFaqMutation } from '../hooks/useFaqsQuery';
import { useFaqCategoriesQuery } from '../hooks/useFaqCategoriesQuery';
import type { Faq, FaqListQuery } from '../types/faq';

const PAGE_SIZE = 20;
const ALL_CATEGORIES = '';

interface AppliedFilters {
  keyword: string;
  categoryId: string;
  page: number;
}

const INITIAL_FILTERS: AppliedFilters = { keyword: '', categoryId: ALL_CATEGORIES, page: 1 };

export const FaqListPage = () => {
  const { t } = useTranslation('common');
  const navigate = useNavigate();

  const [draftKeyword, setDraftKeyword] = useState('');
  const [draftCategoryId, setDraftCategoryId] = useState(ALL_CATEGORIES);
  const [applied, setApplied] = useState<AppliedFilters>(INITIAL_FILTERS);
  const [deleteTarget, setDeleteTarget] = useState<Faq | null>(null);

  const categoriesQuery = useFaqCategoriesQuery();
  const categories = categoriesQuery.data ?? [];

  const listQuery: FaqListQuery = useMemo(
    () => ({
      page: applied.page,
      size: PAGE_SIZE,
      ...(applied.keyword ? { keyword: applied.keyword } : {}),
      ...(applied.categoryId ? { categoryId: Number(applied.categoryId) } : {}),
    }),
    [applied],
  );

  const query = useFaqsQuery(listQuery);
  const { remove } = useFaqMutation();

  const items = query.data?.items ?? [];
  const pagination = query.data?.pagination;

  const categoryNameById = useMemo(
    () => new Map(categories.map((c) => [c.id, c.name] as const)),
    [categories],
  );

  const categoryOptions = useMemo(
    () => [
      { value: ALL_CATEGORIES, label: t('faq.items.filter.categoryAll') },
      ...categories.map((c) => ({ value: String(c.id), label: c.name })),
    ],
    [categories, t],
  );

  const applyFilters = () =>
    setApplied({ keyword: draftKeyword.trim(), categoryId: draftCategoryId, page: 1 });

  const resetFilters = () => {
    setDraftKeyword('');
    setDraftCategoryId(ALL_CATEGORIES);
    setApplied(INITIAL_FILTERS);
  };

  const columns: DataTableColumn<Faq>[] = [
    {
      key: 'category',
      header: t('faq.items.cols.category'),
      width: 160,
      cell: (row) => (
        <span className="text-sm" style={{ color: 'var(--content-muted)' }}>
          {categoryNameById.get(row.categoryId) ?? `#${row.categoryId}`}
        </span>
      ),
    },
    {
      key: 'question',
      header: t('faq.items.cols.question'),
      cell: (row) => (
        <span
          className="block max-w-[420px] truncate text-sm font-medium"
          style={{ color: 'var(--content-strong)' }}
          title={row.question}
        >
          {row.question}
        </span>
      ),
    },
    {
      key: 'viewCount',
      header: t('faq.items.cols.viewCount'),
      align: 'right',
      width: 96,
      cell: (row) => (
        <span className="text-sm tabular-nums" style={{ color: 'var(--content-muted)' }}>
          {row.viewCount.toLocaleString()}
        </span>
      ),
    },
    {
      key: 'sortOrder',
      header: t('faq.items.cols.sortOrder'),
      align: 'right',
      width: 88,
      cell: (row) => (
        <span className="text-sm tabular-nums" style={{ color: 'var(--content-muted)' }}>
          {row.sortOrder}
        </span>
      ),
    },
    {
      key: 'isActive',
      header: t('faq.items.cols.isActive'),
      align: 'center',
      width: 96,
      cell: (row) => (
        <StatusBadge
          variant={row.isActive ? 'success' : 'info'}
          label={row.isActive ? t('faq.active') : t('faq.inactive')}
        />
      ),
    },
    {
      key: 'actions',
      header: '',
      align: 'right',
      width: 56,
      cell: (row) => (
        <RowActions
          triggerLabel={t('faq.items.rowActionsLabel')}
          actions={[
            { key: 'edit', label: t('faq.edit'), onClick: () => navigate(`/faq/items/${row.id}`) },
            {
              key: 'delete',
              label: t('faq.delete'),
              variant: 'danger',
              separatorBefore: true,
              onClick: () => setDeleteTarget(row),
            },
          ]}
        />
      ),
    },
  ];

  return (
    <PageShell
      title={t('faq.items.title')}
      actions={
        <Link to="/faq/items/new">
          <Button size="sm">
            <Plus size={15} aria-hidden="true" />
            {t('faq.items.create')}
          </Button>
        </Link>
      }
    >
      <FilterBar
        className="mb-4"
        filters={
          <>
            <FilterField label={t('faq.items.filter.keyword')}>
              <Input
                type="search"
                value={draftKeyword}
                placeholder={t('faq.items.filter.keywordPlaceholder')}
                onChange={(e) => setDraftKeyword(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') applyFilters(); }}
              />
            </FilterField>
            <FilterField label={t('faq.items.filter.category')}>
              <RadixSelect
                value={draftCategoryId}
                onChange={setDraftCategoryId}
                options={categoryOptions}
                aria-label={t('faq.items.filter.category')}
              />
            </FilterField>
          </>
        }
        actions={
          <>
            <Button size="sm" onClick={applyFilters}>{t('faq.items.filter.search')}</Button>
            <Button size="sm" variant="outline" onClick={resetFilters}>{t('faq.items.filter.reset')}</Button>
          </>
        }
      />

      {query.isPending ? (
        <div role="status" aria-live="polite" className="space-y-2">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} className="h-[44px] w-full" />
          ))}
        </div>
      ) : query.isError ? (
        <div
          role="alert"
          aria-live="polite"
          className="flex flex-col items-center gap-2 rounded-lg py-12 text-center text-sm"
          style={{ background: 'var(--status-danger-bg)', color: 'var(--status-danger-fg)' }}
        >
          <p>{t('faq.items.loadFailed')}</p>
          <Button variant="outline" size="sm" onClick={() => query.refetch()}>
            {t('faq.retry')}
          </Button>
        </div>
      ) : (
        <DataTable
          columns={columns}
          rows={items}
          rowKey={(row) => row.id}
          emptyState={t('faq.items.empty')}
          stickyHeader
        />
      )}

      {pagination ? (
        <Pagination
          total={pagination.totalItems}
          currentPage={applied.page}
          pageSize={PAGE_SIZE}
          onPageChange={(p) => setApplied((prev) => ({ ...prev, page: p }))}
          className="mt-4"
        />
      ) : null}

      <Dialog open={deleteTarget !== null} onOpenChange={(open) => { if (!open) setDeleteTarget(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('faq.items.deleteTitle')}</DialogTitle>
            <DialogDescription>
              {t('faq.items.deleteDesc', { question: deleteTarget?.question ?? '' })}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="ghost" size="sm">{t('faq.cancel')}</Button>
            </DialogClose>
            <Button
              variant="danger"
              size="sm"
              disabled={remove.isPending}
              onClick={() => {
                if (deleteTarget) remove.mutate(deleteTarget.id, { onSuccess: () => setDeleteTarget(null) });
              }}
            >
              {t('faq.delete')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PageShell>
  );
};
